import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { useApi, formatDate } from "@/lib/api";
import { useSeo } from "@/lib/site";
import { Loading, NotFoundBlock } from "@/components/site/Loading";
import { RichText } from "@/components/site/RichText";
import { ArticleCard } from "@/components/site/ArticleCard";
import { ServiceCard } from "@/components/site/ServiceCard";

const Box = ({ title, items, tone, testId }) => (
  <section className={`rounded-3xl ${tone} p-6 md:p-8 mt-10`} data-testid={testId}>
    <h2 className="text-2xl mb-4">{title}</h2>
    <ul className="space-y-3">{items.map((t) => <li key={t} className="flex gap-3"><span className="mt-2.5 h-2 w-2 rounded-full bg-ink/60 shrink-0" aria-hidden="true" />{t}</li>)}</ul>
  </section>
);

export default function ArticlePage({ hub }) {
  const { slug } = useParams();
  const { data: a, loading, error } = useApi(`/articles/${slug}`);
  useSeo(a?.title, a?.summary);
  if (loading) return <Loading />;
  if (error || !a || a.hub !== hub) return <NotFoundBlock title="We couldn't find that article." />;
  const hubName = hub === "direction" ? "Direction" : "Understanding";
  return (
    <article className="pt-10" data-testid="article-page">
      <div className="container-site">
        <Link to={`/${hub}`} className="btn-ghost text-sm" data-testid="article-back-link"><ArrowLeft className="h-4 w-4" aria-hidden="true" /> {hubName}</Link>
        <header className="mt-8 max-w-4xl">
          <p className="eyebrow mb-4">{a.category_name} · {a.reading_minutes} min read</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="article-title">{a.title}</h1>
          <p className="mt-6 text-lg md:text-xl text-ink-soft max-w-3xl">{a.intro}</p>
          <div className="mt-6 flex flex-wrap items-center gap-3 text-sm text-ink-muted" data-testid="article-dates">
            <span>Published {formatDate(a.published_at)}</span>
          </div>
        </header>
      </div>
      <div className="container-site mt-6 grid gap-12 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="max-w-3xl" data-testid="article-body">
          <RichText text={a.body} />
          {a.considerations?.length > 0 && <Box title="Key considerations" items={a.considerations} tone="bg-sage-light" testId="article-considerations" />}
          {a.next_steps?.length > 0 && <Box title="Suggested next steps" items={a.next_steps} tone="bg-teal-light" testId="article-next-steps" />}
          {a.sources?.length > 0 && (
            <section className="mt-10 text-sm text-ink-soft" data-testid="article-sources">
              <h2 className="text-lg mb-2">Further reading from trusted sources</h2>
              <ul className="space-y-1.5">{a.sources.map((s) => <li key={s.url}><a href={s.url} target="_blank" rel="noopener noreferrer" className="btn-ghost !font-medium">{s.label} <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" /><span className="sr-only">(opens in a new tab)</span></a></li>)}</ul>
            </section>
          )}
        </div>
        <aside className="space-y-5 lg:sticky lg:top-28 self-start" aria-label="Relevant support">
          <div className="rounded-3xl bg-clay-light p-6 text-[0.95rem]">
            <p className="font-semibold">Not an emergency service</p>
            <p className="mt-1 text-ink-soft">If someone is in immediate danger, call <a href="tel:999" className="underline font-semibold">999</a>. For urgent medical advice, call <a href="tel:111" className="underline font-semibold">111</a>.</p>
          </div>
          {a.related_services.slice(0, 2).map((s) => <ServiceCard key={s.slug} entry={s} compact />)}
        </aside>
      </div>
      {a.related_articles?.length > 0 && (
        <section className="container-site mt-20" aria-labelledby="related-heading" data-testid="article-related">
          <h2 id="related-heading" className="text-3xl mb-6">Related articles</h2>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">{a.related_articles.map((r) => <ArticleCard key={r.slug} article={r} />)}</div>
        </section>
      )}
    </article>
  );
}
